import { useEffect, useRef, useState } from 'react'
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons'
import { Alert, Button, Icon, Text, Toggle } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import {
  activityOutcome,
  answeredNoActivityMonths,
  fetchAskableMonths,
  isNoActivityOutcome,
  isNotConfirmableOutcome,
  noActivityOutcome,
  notConfirmableDisplay,
  notConfirmableOutcome,
  parseEmptyMonthsInput,
  pickedFromOutcome,
  recordActivity,
  recordNoActivity,
  toChipGroups,
  toQuestion,
  toSummary,
  type AskableMonths,
} from './emptyMonths'
import type { WidgetProps } from './types'
import { QuestionHeading } from './cardParts'
import { WidgetCard } from './WidgetCard'

const LEAD_IN = 'Heard has no activity on this account for a stretch.'
const START_MONTH_NOTE = 'Heard will ask for statements from that month on.'

export const EmptyMonthsWidget = ({
  input,
  resolved,
  outcome,
  onOutcome,
}: WidgetProps) => {
  const [step, setStep] = useState<'ask' | 'pick'>('ask')
  const [picked, setPicked] = useState<string[]>([])
  const [recording, setRecording] = useState(false)
  const [failedAnswer, setFailedAnswer] = useState<'none' | 'some' | null>(null)
  const [askable, setAskable] = useState<AskableMonths | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const reportedNotConfirmable = useRef(false)
  const [notConfirmableRefused, setNotConfirmableRefused] = useState(false)
  const parsed = parseEmptyMonthsInput(input)
  const askedAccountId = parsed?.financialAccountId
  // Keyed on content, not identity: parsing builds a new array every render.
  const askedMonthsKey = parsed?.months.join(',') ?? ''

  // The model can ask about months the status says need a statement, so the
  // card asks only about the ones it still lets the member clear.
  useEffect(() => {
    let cancelled = false
    if (!resolved && askedAccountId !== undefined && askedMonthsKey !== '') {
      setLoadFailed(false)
      fetchAskableMonths(askedAccountId, askedMonthsKey.split(','))
        .then((months) => !cancelled && setAskable(months))
        .catch(() => !cancelled && setLoadFailed(true))
    }
    return () => {
      cancelled = true
    }
  }, [resolved, askedAccountId, askedMonthsKey, loadAttempt])

  const reportNotConfirmable = async (
    accountLabel: string,
    stillNeeded: string[]
  ) => {
    reportedNotConfirmable.current = true
    setNotConfirmableRefused(false)
    const accepted = await onOutcome(
      notConfirmableOutcome(accountLabel, stillNeeded),
      notConfirmableDisplay(accountLabel, stillNeeded)
    )
    if (accepted === false) {
      reportedNotConfirmable.current = false
      setNotConfirmableRefused(true)
    }
  }

  useEffect(() => {
    if (
      !parsed ||
      resolved ||
      askable?.confirmable.length !== 0 ||
      reportedNotConfirmable.current ||
      notConfirmableRefused
    )
      return
    void reportNotConfirmable(parsed.accountLabel, askable.stillNeeded)
  })

  if (!parsed || isNotConfirmableOutcome(outcome)) return null
  const { financialAccountId, accountLabel, remainingCount } = parsed

  if (!resolved && notConfirmableRefused && askable) {
    return (
      <Alert
        variant="error"
        description="Heard couldn't save this answer."
        ctaText="Try again"
        ctaOnClick={() =>
          void reportNotConfirmable(accountLabel, askable.stillNeeded)
        }
      />
    )
  }

  if (!resolved && loadFailed) {
    return (
      <Alert
        variant="error"
        description="Heard couldn't load these months."
        ctaText="Try again"
        ctaOnClick={() => setLoadAttempt((attempt) => attempt + 1)}
      />
    )
  }
  if (!resolved && !askable?.confirmable.length) return null
  const months = resolved ? parsed.months : (askable?.confirmable ?? [])

  if (resolved && isNoActivityOutcome(outcome)) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-[12px] border border-neutral-200 bg-neutral-25 px-4 py-3">
        <div className="flex min-w-0 items-start gap-2">
          <Icon icon={faCircleCheck} className="mt-0.5" />
          <div className="flex flex-col">
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              {toSummary(answeredNoActivityMonths(outcome, months).length)}
            </Text>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              {accountLabel}
            </Text>
          </div>
        </div>
        {remainingCount > 0 && (
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            className="shrink-0 whitespace-nowrap"
          >
            {remainingCount} left to go through
          </Text>
        )}
      </div>
    )
  }

  const locked = resolved || recording
  const answeredPicks = resolved ? pickedFromOutcome(outcome, months) : null
  const shownPicked = answeredPicks ?? picked
  // Reported only once the write lands, so an answer never stands for months
  // Heard failed to record.
  const report = async (answer: 'none' | 'some') => {
    setRecording(true)
    setFailedAnswer(null)
    try {
      if (answer === 'none') {
        await recordNoActivity(financialAccountId, months)
        onOutcome(noActivityOutcome(accountLabel, months))
      } else {
        await recordActivity({
          financialAccountId,
          askedMonths: months,
          activityMonths: shownPicked,
        })
        onOutcome(activityOutcome(accountLabel, shownPicked))
      }
    } catch {
      setFailedAnswer(answer)
    } finally {
      setRecording(false)
    }
  }
  const reportNoActivity = () => report('none')
  const failure = failedAnswer && (
    <Alert
      variant="error"
      description="Heard couldn't save that answer."
      ctaText="Try again"
      ctaOnClick={() => report(failedAnswer)}
    />
  )

  // The card says this itself, so a question it decides not to ask leaves no
  // sentence on screen pointing at it.
  const leadIn = (
    <Text
      variant={TypographyStyles.BODY_SM}
      className="leading-relaxed text-neutral-600"
    >
      {LEAD_IN}
    </Text>
  )

  if (step === 'ask' && !answeredPicks) {
    return (
      <>
        {leadIn}
        <WidgetCard>
          <div className="flex flex-col gap-1">
            <QuestionHeading>{toQuestion(months)}</QuestionHeading>
            <Text
              variant={TypographyStyles.BODY_SM}
              color={TextColors.MUTED}
              className="pl-11"
            >
              {accountLabel}
            </Text>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={locked} onClick={reportNoActivity}>
              No, nothing then
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={locked}
              onClick={() => setStep('pick')}
            >
              Yes, some
            </Button>
          </div>
          {failure}
        </WidgetCard>
      </>
    )
  }

  // A resolved card cannot tell which asked months it offered, so it redraws only the answer.
  const groups = toChipGroups(answeredPicks ?? months)
  const toggle = (month: string) =>
    setPicked((current) => (current.includes(month) ? [] : [month]))

  return (
    <>
      {leadIn}
      <WidgetCard>
        <QuestionHeading>Which month did the activity start?</QuestionHeading>
        <div className="flex flex-col gap-3">
          {groups.map(({ year, chips }) => (
            <div key={year ?? 'months'} className="flex flex-col gap-2">
              {year && (
                <Text
                  variant={TypographyStyles.BODY_XS}
                  color={TextColors.MUTED}
                >
                  {year}
                </Text>
              )}
              <div className="flex flex-wrap gap-2">
                {chips.map(({ month, label, name }) => {
                  const pressed = shownPicked.includes(month)
                  return (
                    <Toggle
                      key={month}
                      variant="outline"
                      size="sm"
                      aria-label={name}
                      checked={pressed}
                      disabled={locked}
                      onCheckedChange={() => toggle(month)}
                      className="body-sm min-w-14 disabled:opacity-100"
                    >
                      {label}
                    </Toggle>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-start gap-2 border-t border-neutral-100 pt-4">
          {shownPicked.length === 0 ? (
            <Button size="sm" disabled={locked} onClick={reportNoActivity}>
              None of them
            </Button>
          ) : (
            <Button size="sm" disabled={locked} onClick={() => report('some')}>
              Done
            </Button>
          )}
          {shownPicked.length > 0 && (
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              {START_MONTH_NOTE}
            </Text>
          )}
        </div>
        {failure}
      </WidgetCard>
    </>
  )
}
